import Experience from "@folio-2026/experience"
import {
  EVENTS,
  MSDFTextGeometry,
  MSDFTextNodeMaterial,
  oklchToHex,
  rgbToHex,
  rgbToHex256,
} from "@folio-2026/shared"
import { gsap } from "gsap/all"
import { InteractionManager } from "three.interactive"
import { attribute, color, mix, step, uniform } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

type TextProps = {
  name: string
  scene: THREE.Scene
  element: HTMLElement
  atlas: THREE.Texture
  font: MSDFFont
  shouldUpdate: boolean
  preventInitialUpdate?: boolean
  uniforms: any
  interactionManager?: InteractionManager
  debugFolder?: FolderApi
}

export type MSDFFont = {
  data: object
}

export default class Text {
  experience: Experience
  scene: THREE.Scene
  element: HTMLElement
  text: string
  name: string

  computedStyle: CSSStyleDeclaration
  font!: MSDFFont
  bounds!: DOMRect

  geometry!: MSDFTextGeometry
  material!: MSDFTextNodeMaterial
  mesh!: THREE.Mesh
  proxyMesh!: THREE.Mesh
  y!: number

  shouldUpdate: boolean = true

  uniforms: any
  private updateCounter: number = 0
  private lastScrambledText: string = ""
  private interactionManager?: InteractionManager
  visibleWorlds: number = 0
  visibleWorldsUniform!: ReturnType<typeof uniform>
  hoverUniform!: ReturnType<typeof uniform>
  hoverColorNode!: ReturnType<typeof color>
  private revealScrambleTween?: gsap.core.Tween
  private revealFadeTween?: gsap.core.Tween
  private leaveScrambleTween?: gsap.core.Tween
  private leaveFadeTween?: gsap.core.Tween
  private debugFolder?: FolderApi
  /** Noise params passed to material; same refs used by setDebugFolder bindings */
  noiseDebugParams!: {
    noiseUvFrequency: THREE.UniformNode<number>
    noiseUvAmplitude: THREE.UniformNode<number>
  }

  constructor({
    name,
    scene,
    element,
    uniforms,
    atlas,
    font,
    shouldUpdate,
    preventInitialUpdate,
    interactionManager,
    debugFolder,
  }: TextProps) {
    this.experience = Experience.getInstance()

    this.scene = scene
    this.element = element
    this.text = this.element.textContent
    this.name = name

    this.interactionManager = interactionManager
    this.debugFolder = debugFolder

    this.shouldUpdate = shouldUpdate

    this.uniforms = {
      ...uniforms,
      progress: uniform(0),
      fade: uniform(this.text.length > 50),
    }

    this.computedStyle = window.getComputedStyle(this.element) // Saving initial computed style.

    this.createBounds()

    this.createMesh(atlas, font)

    this.experience.on(EVENTS.Loader.CurtainRemoved, () => {
      if (preventInitialUpdate) {
        return
      }

      this.resize()
      if (this.shouldUpdate) {
        this.update()
      }
    })

    // Set up event listeners after mesh is created and positioned
    // this.setupEventListeners()
  }

  hover(isHover: boolean = false) {
    // Update hover uniform (0 = not hovered, 1 = hovered)
    this.hoverUniform.value = isHover ? 1 : 0
  }

  private createMesh(atlas: THREE.Texture, font: MSDFFont) {
    const {
      align,
      lineHeight,
      width,
      height,
      letterSpacing,
      color: hexColor,
    } = this.getStyles()

    this.geometry = new MSDFTextGeometry({
      text: this.text,
      font: font.data,
      lineHeight,
      letterSpacing,
      width,
      height,
      align,
      tabSize: 8,
      mode: this.text.length < 50 ? "pre" : undefined,
    })

    const visibleWorldsUniform = uniform(0)
    this.visibleWorldsUniform = visibleWorldsUniform

    // Create hover uniform for mixing hover color
    const hoverUniform = uniform(0)
    this.hoverUniform = hoverUniform

    // Noise debug params: always pass to material so setDebugFolder can bind to the same refs later
    this.noiseDebugParams = {
      noiseUvFrequency: uniform(1.6),
      noiseUvAmplitude: uniform(1),
    }

    this.material = new MSDFTextNodeMaterial({
      isSmooth: 1,
      strokeOutsetWidth: 0,
      strokeInsetWidth: 0,
      threshold: 0.5,
      map: atlas,
      fade: this.uniforms.fade,
      progress: this.uniforms.progress,
      color: color(hexColor),
      noiseTexture: this.experience.globalResources.perlin as THREE.Texture,
      ...this.noiseDebugParams,
    })

    if (this.debugFolder) {
      this.setDebugFolder(this.debugFolder)
    }

    // Modify colorNode to apply darker color to scrambled words
    const originalColor = this.material.color
    const wordIndexAttr = attribute("wordIndex", "float")
    // step(a, b) returns 1 if b >= a, 0 otherwise
    // We want isScrambled = 1 when wordIndex >= visibleWorlds
    // This correctly handles both single-word (wordIndex 0 scrambled when visibleWorldsUniform = 0)
    // and multi-word cases (wordIndex 1+ scrambled when visibleWorldsUniform <= wordIndex)
    const isScrambled = step(visibleWorldsUniform, wordIndexAttr)
    const darkerColor = originalColor.mul(0.2) // 20% brightness for scrambled words

    // Create hover color (brighter version - using 2x for more visible change)
    this.hoverColorNode = originalColor.mul(1.5)

    // Build colorNode with hover mixing
    // First mix between original and darker based on scrambled state
    const normalColor = mix(originalColor, darkerColor, isScrambled)
    // Then mix between normal color and hover color based on hover state
    this.material.colorNode = mix(
      normalColor,
      this.hoverColorNode,
      hoverUniform,
    )

    // this.material.outputNode = Fn(() => {
    //   const color = vec3(1, 0, 0)
    //   const alpha = 1
    //   return vec4(color, alpha)
    // })()

    this.material.side = THREE.DoubleSide

    this.mesh = new THREE.Mesh(this.geometry, this.material)
    this.mesh.scale.set(
      this.uniforms.scale,
      this.uniforms.scale,
      this.uniforms.scale,
    )

    this.mesh.rotateX(Math.PI)

    this.scene.add(this.mesh)

    // Create proxy mesh for interaction detection
    this.createProxyMesh()
    // Position proxy mesh initially
    this.initialPositionProxyMesh()
  }

  private createProxyMesh() {
    const proxyGeometry = new THREE.PlaneGeometry(
      this.bounds.width,
      this.bounds.height,
    )
    // Offset geometry to start from bottom-left (like the text mesh) instead of center
    proxyGeometry.translate(this.bounds.width / 2, this.bounds.height / -2, 0)

    const proxyMaterial = new THREE.MeshBasicMaterial({
      visible: true, // Invisible for production, set to true for debugging
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
    })
    this.proxyMesh = new THREE.Mesh(proxyGeometry, proxyMaterial)
    this.proxyMesh.scale.set(
      this.uniforms.scale,
      this.uniforms.scale,
      this.uniforms.scale,
    )
    this.proxyMesh.visible = this.mesh.visible // Match main mesh visibility
    this.proxyMesh.rotateX(Math.PI)

    this.scene.add(this.proxyMesh)
  }

  private getStyles() {
    const {
      fontSize,
      lineHeight,
      whiteSpace,
      textAlign,
      letterSpacing,
      color,
    } = this.computedStyle

    let hexColor: string | undefined
    //  color.startsWith("oklch") ? oklchToHex(color) : color.startsWith('#') ? color : throw new Error('Invalid color format')
    if (color.startsWith("oklch")) {
      hexColor = oklchToHex(color)
    } else if (color.startsWith("#")) {
      hexColor = color
    } else if (color.startsWith("rgb")) {
      const r = parseFloat(color.split("(")[1].split(",")[0])
      const g = parseFloat(color.split("(")[1].split(",")[1])
      const b = parseFloat(color.split("(")[1].split(",")[2].split(")")[0])
      const isNormalized = r <= 1 && g <= 1 && b <= 1
      hexColor = isNormalized ? rgbToHex({ r, g, b }) : rgbToHex256({ r, g, b })
    }

    const fontSizeNum = window.parseFloat(fontSize)

    return {
      fontSize: fontSizeNum,
      lineHeight: parseFloat(lineHeight),
      letterSpacing: parseFloat(letterSpacing),
      // Round up + buffer so layout doesn't wrap the last character (sub-pixel, measure break, font load timing)
      width:
        Math.ceil(this.bounds.width) +
        Math.max(6, Math.ceil(this.bounds.width * 0.02)),
      height: Math.ceil(this.bounds.height),
      align: textAlign,
      whiteSpace: whiteSpace,
      color: hexColor,
    }
  }

  createBounds() {
    this.bounds = this.element.getBoundingClientRect()
    // Menu is a fixed overlay: use viewport-only position (no scroll)
    this.y =
      this.name === "menu"
        ? this.bounds.top
        : this.bounds.top + this.experience.scrollInfo.actualScroll
  }

  update() {
    if (!this.mesh) {
      return
    }

    const { width, height } = this.experience.sizes
    const positionX = this.bounds.left - width / 2
    let positionY: number
    if (this.name === "menu") {
      // Fixed overlay: viewport-only positioning
      positionY = height / 2 - this.bounds.top - this.bounds.height
    } else {
      if (!this.shouldUpdate) return
      positionY =
        -this.y +
        this.experience.scrollInfo.animatedScroll +
        height / 2 -
        this.bounds.height
    }
    this.mesh.position.x = positionX
    this.mesh.position.y = positionY

    // Update proxy mesh position to match main mesh
    if (this.proxyMesh) {
      this.proxyMesh.position.y = positionY
      this.proxyMesh.position.x = positionX
      // Keep proxy mesh visible for interaction detection even if main mesh is not visible
      // The visibility check in event handlers will prevent firing when main mesh is hidden
      this.proxyMesh.visible = true
    }
  }

  private initialPositionProxyMesh() {
    if (!this.proxyMesh) {
      return
    }

    const { width, height } = this.experience.sizes
    const positionX = this.bounds.left - width / 2
    const positionY =
      this.name === "menu"
        ? height / 2 - this.bounds.top - this.bounds.height
        : -this.y +
          this.experience.scrollInfo.actualScroll +
          height / 2 -
          this.bounds.height

    this.proxyMesh.position.x = positionX
    this.proxyMesh.position.y = positionY
    this.proxyMesh.visible = this.mesh.visible

    // Menu texts use preventUpdate so update() never runs; set main mesh position once
    if (this.name === "menu") {
      this.mesh.position.x = positionX
      this.mesh.position.y = positionY
    }
  }

  textScramble(text: string, progress: number) {
    // Split by spaces and newlines, preserving delimiters
    const tokens = text.split(/(\s+|\n+)/)
    const wordCount = Math.ceil(tokens.length / 2)
    const visibleWorlds = wordCount - Math.ceil(wordCount * (1 - progress))

    // Store visibleWorlds and update uniform
    this.visibleWorlds = visibleWorlds
    this.visibleWorldsUniform.value = visibleWorlds

    const chars = "jompaWB)#@$"
    const charsLength = chars.length
    let wordIndex = 0
    let scrubled = ""

    // Single pass: process tokens directly
    for (let i = 0; i < tokens.length; i++) {
      if (i % 2 === 0) {
        const world = tokens[i] // words
        const delimiter = tokens[i + 1] || ""

        // For single word, scramble when progress < 1
        if (wordCount === 1) {
          if (progress >= 1) {
            scrubled += world + delimiter
          } else {
            // Scramble the single word
            const wordLength = world.length
            let randomWord = ""
            for (let j = 0; j < wordLength; j++) {
              randomWord += chars[Math.floor(Math.random() * charsLength)]
            }
            scrubled += randomWord + delimiter
          }
        } else {
          if (wordIndex <= visibleWorlds) {
            scrubled += world + delimiter
          } else {
            // Random word
            const wordLength = world.length
            let randomWord = ""
            for (let j = 0; j < wordLength; j++) {
              randomWord += chars[Math.floor(Math.random() * charsLength)]
            }
            scrubled += randomWord + delimiter
          }
        }
        wordIndex++
      }
    }

    return scrubled
  }

  reveal() {
    // Kill any running animations
    if (this.revealScrambleTween) {
      this.revealScrambleTween.kill()
    }
    if (this.revealFadeTween) {
      this.revealFadeTween.kill()
    }
    if (this.leaveScrambleTween) {
      this.leaveScrambleTween.kill()
    }
    if (this.leaveFadeTween) {
      this.leaveFadeTween.kill()
    }

    // Ensure mesh is visible so the reveal animation can run (resetToRevealStart hides it)
    if (this.mesh) this.mesh.visible = true
    if (this.proxyMesh) this.proxyMesh.visible = true

    // Reset state to starting values for reveal animation
    this.uniforms.progress.value = 0
    this.visibleWorlds = 0
    this.visibleWorldsUniform.value = 0

    const proxy = { progress: 0 }

    this.updateCounter = 0
    this.lastScrambledText = ""

    const customRevealDuration = this.element.getAttribute(
      "data-reveal-duration",
    )
    const revealDuration = customRevealDuration
      ? parseFloat(customRevealDuration)
      : 0.6

    this.revealScrambleTween = gsap.to(proxy, {
      progress: 1,
      duration: revealDuration,
      ease: "none",

      onUpdate: () => {
        this.updateCounter++
        // Only call textScramble every 5 onUpdate calls (or on first call)
        if (this.updateCounter % 5 === 0 || this.lastScrambledText === "") {
          this.lastScrambledText = this.textScramble(this.text, proxy.progress)
        }

        this.geometry.update({
          text: this.lastScrambledText,
        })
      },
      onComplete: () => {
        // Set visibleWorlds to a high value so all words are visible (not scrambled)
        const tokens = this.text.split(/(\s+|\n+)/)
        const wordCount = Math.ceil(tokens.length / 2)
        this.visibleWorlds = wordCount
        this.visibleWorldsUniform.value = wordCount

        this.geometry.update({
          text: this.text,
        })
        this.revealScrambleTween = undefined
      },
    })

    const fadeProxy = { progress: 0 }

    this.revealFadeTween = gsap.to(fadeProxy, {
      progress: 1,
      duration: 1,
      ease: "power2.out",

      onUpdate: () => {
        this.uniforms.progress.value = fadeProxy.progress
      },
      onComplete: () => {
        this.revealFadeTween = undefined
      },
    })
  }

  /**
   * Resets the text to the same state as at the start of reveal() (hidden).
   * Call before reveal() when re-entering the section so the reveal always
   * starts from a consistent hidden state (e.g. after user scrolled away mid-reveal).
   */
  resetToRevealStart() {
    if (this.revealScrambleTween) {
      this.revealScrambleTween.kill()
      this.revealScrambleTween = undefined
    }
    if (this.revealFadeTween) {
      this.revealFadeTween.kill()
      this.revealFadeTween = undefined
    }
    if (this.leaveScrambleTween) {
      this.leaveScrambleTween.kill()
      this.leaveScrambleTween = undefined
    }
    if (this.leaveFadeTween) {
      this.leaveFadeTween.kill()
      this.leaveFadeTween = undefined
    }
    this.uniforms.progress.value = 0
    this.visibleWorlds = 0
    this.visibleWorldsUniform.value = 0
    this.updateCounter = 0
    this.lastScrambledText = ""
    this.geometry.update({ text: "" })
    // Hide mesh so it’s fully invisible regardless of material progress/fade (e.g. when fade=true the shader can still show a sliver)
    if (this.mesh) this.mesh.visible = false
    if (this.proxyMesh) this.proxyMesh.visible = false
  }

  /**
   * Kills any in-progress reveal animations and resets the text to hidden state.
   * Use when closing the menu before reveal has finished (e.g. fast click).
   */
  revertReveal() {
    if (this.revealScrambleTween) {
      this.revealScrambleTween.kill()
      this.revealScrambleTween = undefined
    }
    if (this.revealFadeTween) {
      this.revealFadeTween.kill()
      this.revealFadeTween = undefined
    }
    // Always restore to pre-reveal state (hidden), not only when a reveal was in progress
    this.uniforms.progress.value = 0
    this.visibleWorlds = 0
    this.visibleWorldsUniform.value = 0
    this.lastScrambledText = ""
    this.geometry.update({ text: "" })
    if (this.mesh) this.mesh.visible = false
    if (this.proxyMesh) this.proxyMesh.visible = false
  }

  leave() {
    if (!this.text) {
      return
    }

    // Check if text has already been left (progress is 0 or very close to 0)
    if (this.uniforms.progress.value <= 0.01 && this.visibleWorlds === 0) {
      return
    }

    // Kill any running animations
    if (this.revealScrambleTween) {
      this.revealScrambleTween.kill()
    }
    if (this.revealFadeTween) {
      this.revealFadeTween.kill()
    }
    if (this.leaveScrambleTween) {
      this.leaveScrambleTween.kill()
    }
    if (this.leaveFadeTween) {
      this.leaveFadeTween.kill()
    }

    const proxy = { progress: 1 }

    this.updateCounter = 0
    this.lastScrambledText = this.text

    this.leaveScrambleTween = gsap.to(proxy, {
      progress: 0,
      duration: 0.5,
      ease: "none",

      onUpdate: () => {
        this.updateCounter++
        // Only call textScramble every 5 onUpdate calls (or on first call)
        if (
          this.updateCounter % 5 === 0 ||
          this.lastScrambledText === this.text
        ) {
          this.lastScrambledText = this.textScramble(this.text, proxy.progress)
        }

        this.geometry.update({
          text: this.lastScrambledText,
        })
      },
      onComplete: () => {
        // Set visibleWorlds to 0 so all words are scrambled
        this.visibleWorlds = 0
        this.visibleWorldsUniform.value = 0

        this.geometry.update({
          text: "",
        })
        this.leaveScrambleTween = undefined
      },
    })

    const fadeProxy = { progress: 1 }

    this.leaveFadeTween = gsap.to(fadeProxy, {
      progress: 0,
      duration: 1,
      ease: "power2.in",

      onUpdate: () => {
        this.uniforms.progress.value = fadeProxy.progress
      },
      onComplete: () => {
        this.leaveFadeTween = undefined
      },
    })
  }

  setDebugFolder(debugFolder?: FolderApi) {
    this.debugFolder = debugFolder
    if (!this.debugFolder) return

    return

    const preview = this.text.replace(/\s+/g, " ").trim().slice(0, 10)
    const title =
      preview.length > 0
        ? `text: ${preview}${this.text.length > 10 ? "…" : ""}`
        : "text"
    const textFolder = this.debugFolder.addFolder({ title })
    textFolder.addBinding(this.noiseDebugParams.noiseUvFrequency, "value", {
      label: "Noise UV frequency",
      min: 0.01,
      max: 10,
      step: 0.01,
    })
    textFolder.addBinding(this.noiseDebugParams.noiseUvAmplitude, "value", {
      label: "Noise UV amplitude",
      min: 0,
      max: 5,
      step: 0.01,
    })
  }

  setupEventListeners() {
    if (!this.interactionManager || !this.mesh || !this.proxyMesh) {
      return
    }

    // Remove any existing event listeners to prevent duplicates
    this.removeEventListeners()

    const clickEvent = this.element.getAttribute("data-click-event")
    const hoverEvent = this.element.getAttribute("data-hover-event")
    const outEvent = this.element.getAttribute("data-out-event")

    // If no event attributes are present, don't set up listeners
    if (!clickEvent && !hoverEvent && !outEvent) {
      return
    }

    this.interactionManager.add(this.proxyMesh)

    // Detect if device supports touch (mobile)
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error - msMaxTouchPoints is a legacy IE property
      navigator.msMaxTouchPoints > 0

    if (clickEvent) {
      const clickHandler = (event: any) => {
        event.stopPropagation()

        if (this.mesh.visible) {
          this.experience.trigger(clickEvent, [
            {
              ...event,
              htmlElement: this.element,
              meshId: this.proxyMesh.uuid,
              textInstance: this,
            },
          ])
        }
      }

      this.proxyMesh.addEventListener("click" as any, clickHandler)
      // Store handler for potential cleanup
      this.proxyMesh.userData.clickHandler = clickHandler
    }

    // Only add hover/out events on non-touch devices
    if (!isTouchDevice) {
      // Add cursor pointer handlers for clickable text
      if (clickEvent) {
        const cursorOverHandler = () => {
          if (
            this.proxyMesh.visible &&
            this.experience.renderer.instance.domElement
          ) {
            this.experience.renderer.instance.domElement.style.cursor =
              "pointer"
          }
        }

        const cursorOutHandler = () => {
          if (this.experience.renderer.instance.domElement) {
            this.experience.renderer.instance.domElement.style.cursor =
              "default"
          }
        }

        this.proxyMesh.addEventListener("mouseover" as any, cursorOverHandler)
        this.proxyMesh.addEventListener("mouseout" as any, cursorOutHandler)
        // Store handlers for potential cleanup
        this.proxyMesh.userData.cursorOverHandler = cursorOverHandler
        this.proxyMesh.userData.cursorOutHandler = cursorOutHandler
      }
      if (hoverEvent) {
        const hoverHandler = (event: any) => {
          const { target } = event

          if (this.proxyMesh.visible) {
            this.experience.trigger(hoverEvent, [
              {
                target,
                htmlElement: this.element,
                textInstance: this,
              },
            ])
          }
        }
        this.proxyMesh.addEventListener("mouseover" as any, hoverHandler)
        // Store handler for potential cleanup
        this.proxyMesh.userData.hoverHandler = hoverHandler
      }

      if (outEvent) {
        const outHandler = (event: any) => {
          const { target } = event

          if (this.proxyMesh.visible) {
            this.experience.trigger(outEvent, [
              {
                target,
                htmlElement: this.element,
                textInstance: this,
              },
            ])
          }
        }
        this.proxyMesh.addEventListener("mouseout" as any, outHandler)
        // Store handler for potential cleanup
        this.proxyMesh.userData.outHandler = outHandler
      }
    }
  }

  removeEventListeners() {
    if (!this.interactionManager || !this.proxyMesh) {
      return
    }

    // Remove click handler
    if (this.proxyMesh.userData.clickHandler) {
      this.proxyMesh.removeEventListener(
        "click" as any,
        this.proxyMesh.userData.clickHandler,
      )
      delete this.proxyMesh.userData.clickHandler
    }

    // Remove hover handler
    if (this.proxyMesh.userData.hoverHandler) {
      this.proxyMesh.removeEventListener(
        "mouseover" as any,
        this.proxyMesh.userData.hoverHandler,
      )
      delete this.proxyMesh.userData.hoverHandler
    }

    // Remove out handler
    if (this.proxyMesh.userData.outHandler) {
      this.proxyMesh.removeEventListener(
        "mouseout" as any,
        this.proxyMesh.userData.outHandler,
      )
      delete this.proxyMesh.userData.outHandler
    }

    // Remove cursor handlers
    if (this.proxyMesh.userData.cursorOverHandler) {
      this.proxyMesh.removeEventListener(
        "mouseover" as any,
        this.proxyMesh.userData.cursorOverHandler,
      )
      delete this.proxyMesh.userData.cursorOverHandler
    }

    if (this.proxyMesh.userData.cursorOutHandler) {
      this.proxyMesh.removeEventListener(
        "mouseout" as any,
        this.proxyMesh.userData.cursorOutHandler,
      )
      delete this.proxyMesh.userData.cursorOutHandler
    }

    this.interactionManager.remove(this.proxyMesh)
  }

  resize() {
    this.computedStyle = window.getComputedStyle(this.element)
    this.createBounds()

    this.geometry.update({
      width: this.bounds.width,
    })

    // Update proxy mesh geometry to match new bounds
    if (this.proxyMesh) {
      this.proxyMesh.geometry.dispose()
      this.proxyMesh.geometry = new THREE.PlaneGeometry(
        this.bounds.width,
        this.bounds.height,
      )
      this.proxyMesh.geometry.translate(
        this.bounds.width / 2,
        this.bounds.height / -2,
        0,
      )
    }
  }

  async remove() {
    await this.leave()

    // Remove event listeners before disposing
    this.removeEventListeners()

    if (this.mesh) {
      this.scene.remove(this.mesh)
    }
    if (this.proxyMesh) {
      this.scene.remove(this.proxyMesh)
      this.proxyMesh.geometry.dispose()
      if (this.proxyMesh.material instanceof THREE.Material) {
        this.proxyMesh.material.dispose()
      }
    }
    this.geometry.dispose()
    this.material.dispose()
    this.mesh = undefined!
    this.proxyMesh = undefined!
  }
}
